import type { PropsWithChildren } from "react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { StyleProvider } from "@ant-design/cssinjs";
import { App, ConfigProvider } from "antd";

import { theme, wave } from "./_config";
import { Context } from "./_context";
import { useHooks } from "./_hook";
import { localeMap } from "./_locale";

type ProviderProps = PropsWithChildren<{
  portalContainer?: HTMLElement | null;
  styleContainer?: ShadowRoot | HTMLElement | null;
}>;

const fallbackContainer = () =>
  typeof document !== "undefined" ? document.body : undefined;

/**
 * Intercepts Ant Design style injection and redirects to Shadow DOM
 * @ant-design/cssinjs injects styles via document.head even with StyleProvider.container
 * This observer moves those styles to the target container
 */
const createStyleObserver = (targetContainer: ShadowRoot | HTMLElement) => {
  const movedStyles = new Set<HTMLStyleElement>();

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (
          node instanceof HTMLStyleElement &&
          node.hasAttribute("data-rc-order") &&
          !movedStyles.has(node)
        ) {
          // Move antd style from document.head to shadow DOM
          movedStyles.add(node);
          node.remove();
          targetContainer.appendChild(node);
        }
      }
    }
  });

  observer.observe(document.head, {
    childList: true,
  });

  return observer;
};

export default function ({
  children,
  portalContainer,
  styleContainer,
}: ProviderProps) {
  const observerRef = useRef<MutationObserver | null>(null);

  /** useTranslation */
  const { i18n } = useTranslation();

  /** useHooks */
  const { context } = useHooks();

  /** container */
  const overlayHost = portalContainer ?? fallbackContainer();

  const getPopupContainer = () =>
    overlayHost ?? document.getElementById("ricoh-chat-client");

  /** Setup style observer for Shadow DOM */
  useEffect(() => {
    if (!styleContainer) return;

    // Create observer to intercept and move antd styles
    observerRef.current = createStyleObserver(styleContainer);

    // Move any existing antd styles to shadow DOM
    const existingStyles = document.head.querySelectorAll<HTMLStyleElement>(
      "style[data-rc-order]",
    );
    existingStyles.forEach((style) => {
      style.remove();
      styleContainer.appendChild(style);
    });

    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, [styleContainer]);

  return (
    <StyleProvider container={styleContainer} hashPriority="high" ssrInline>
      <ConfigProvider
        theme={theme}
        wave={wave}
        locale={localeMap[i18n.language]}
        getPopupContainer={getPopupContainer}
      >
        <App
          message={{
            maxCount: 1,
            getContainer: () => overlayHost ?? document.body,
          }}
          notification={{
            showProgress: true,
            pauseOnHover: true,
            getContainer: () => overlayHost ?? document.body,
          }}
          style={{ width: "100%", height: "100%" }}
        >
          <Context.Provider value={context}>{children}</Context.Provider>
        </App>
      </ConfigProvider>
    </StyleProvider>
  );
}
